import db from '../../lib/db';

import type { AuthorId, WorkspaceId } from '../core/types';
import type { EchoRendererProfile } from './wire';

export const ECHO_RENDERER_PROFILE_PROJECTION = '__echo_renderer_profile_projection';

type ProfileKey = [WorkspaceId, AuthorId];
type ProfileRow = Record<string, unknown>;

export interface EchoProfileTable {
  bulkGet(keys: ProfileKey[]): Promise<(ProfileRow | undefined)[]>;
  bulkPut(profiles: ProfileRow[]): Promise<unknown>;
}

export interface EchoProfileDatabase {
  readonly profiles: EchoProfileTable;
  transaction<T>(mode: 'rw', table: EchoProfileTable, operation: () => Promise<T>): Promise<T>;
}

export interface EchoProfileStore {
  mergeRendererProfiles(profiles: readonly EchoRendererProfile[]): Promise<void>;
}

export function createEchoProfileStore(database: EchoProfileDatabase = db as unknown as EchoProfileDatabase): EchoProfileStore {
  return {
    async mergeRendererProfiles(profiles) {
      if (profiles.length === 0) return;

      await database.transaction('rw', database.profiles, async () => {
        const keys = profiles.map(profileKey);
        const existingProfiles = await database.profiles.bulkGet(keys);
        const mergedProfiles = profiles.map((profile, index) => mergeRendererProfile(existingProfiles[index], profile));
        await database.profiles.bulkPut(mergedProfiles);
      });
    },
  };
}

export async function profileRowsNeedRefresh(profileTable: EchoProfileTable, profiles: readonly ProfileRow[]): Promise<boolean> {
  const fullProfiles = profiles.filter(isFullProfilePayload);
  if (fullProfiles.length === 0) return false;

  const existingProfiles = await profileTable.bulkGet(fullProfiles.map((profile) => [String(profile.workspace_id), String(profile.id)]));
  return existingProfiles.some((profile) => !isCompleteProfileRow(profile));
}

function mergeRendererProfile(existingProfile: ProfileRow | undefined, rendererProfile: EchoRendererProfile): ProfileRow {
  const mergedProfile = existingProfile ? { ...existingProfile, ...rendererProfile } : { ...rendererProfile };
  if (isCompleteProfileRow(existingProfile)) {
    delete mergedProfile[ECHO_RENDERER_PROFILE_PROJECTION];
  } else {
    mergedProfile[ECHO_RENDERER_PROFILE_PROJECTION] = true;
  }
  return mergedProfile;
}

function profileKey(profile: EchoRendererProfile): ProfileKey {
  return [profile.workspace_id, profile.id];
}

function isFullProfilePayload(profile: ProfileRow): boolean {
  return isFiniteNumber(profile.created_at);
}

function isCompleteProfileRow(profile: ProfileRow | undefined): boolean {
  if (!profile) return false;
  return (
    isFiniteNumber(profile.created_at) &&
    isFiniteNumber(profile.updated_at) &&
    Object.hasOwn(profile, 'agent_rules') &&
    Object.hasOwn(profile, 'preferences') &&
    Object.hasOwn(profile, 'theme') &&
    Object.hasOwn(profile, 'notification_preference') &&
    profile[ECHO_RENDERER_PROFILE_PROJECTION] !== true
  );
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}
